import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCode, FiSmartphone, FiLayout, FiFeather, FiCpu, FiArrowRight, FiCheck, FiShield } from 'react-icons/fi';

// FRIEND'S CONTACT PAGE - UPDATE THIS URL
const FRIEND_CONTACT_URL = 'https://your-friends-startup.com/contact';

const services = [
  {
    icon: <FiCode size={32} />,
    title: 'Web Development',
    tagline: 'Engineered for performance',
    desc: 'We build fast, scalable, SEO-optimized websites using the latest frontend and backend technologies. Every line of code is crafted for maintainability and speed.',
    features: ['Custom CMS integration', 'Performance-first architecture', 'SEO optimization', 'Responsive across all devices', 'Third-party API integration'],
    tech: ['React', 'Next.js', 'Node.js', 'TypeScript'],
    price: 'Basic: starting from ₹5,000',
    priceNote: 'Advanced: based on requirement',
    externalQuote: null,
  },
  {
    icon: <FiSmartphone size={32} />,
    title: 'Web App Development',
    tagline: 'Complex made simple',
    desc: 'Full-stack web applications with real-time features, authentication, complex business logic, and polished user interfaces that delight users.',
    features: ['Real-time functionality', 'Authentication & authorization', 'Payment gateway integration', 'Dashboard & analytics', 'Cloud deployment'],
    tech: ['React', 'GraphQL', 'PostgreSQL', 'AWS'],
    price: 'Basic: starting from ₹30,000',
    priceNote: 'Advanced: based on requirement',
    externalQuote: null,
  },
  {
    icon: <FiLayout size={32} />,
    title: 'UI/UX Design',
    tagline: 'Design that converts',
    desc: 'Human-centered design rooted in research and psychology. We create interfaces that are both beautiful and effective, driving engagement and conversions.',
    features: ['User research & personas', 'Wireframing & prototyping', 'Design systems', 'Usability testing', 'Handoff to development'],
    tech: ['Figma', 'Protopie', 'Maze', 'Hotjar'],
    price: 'Starting from ₹10,000',
    priceNote: 'Advanced: based on requirement',
    externalQuote: null,
  },
  {
    icon: <FiFeather size={32} />,
    title: 'Branding',
    tagline: 'Identity that endures',
    desc: 'Complete brand identity systems from strategy to execution — logos, typography, color palettes, packaging, and comprehensive brand guidelines.',
    features: ['Brand strategy', 'Logo design', 'Typography system', 'Color palette', 'Brand guidelines book'],
    tech: ['Illustrator', 'Photoshop', 'InDesign', 'Figma'],
    price: 'Starting from ₹10,000',
    priceNote: 'Advanced: based on requirement',
    externalQuote: FRIEND_CONTACT_URL,
  },
  {
    icon: <FiCpu size={32} />,
    title: 'Software Development',
    tagline: 'Built for scale',
    desc: 'Custom software solutions — from CRM systems to automation platforms — engineered with clean architecture, thorough testing, and long-term maintainability.',
    features: ['Custom business logic', 'API development', 'Database design', 'Automated testing', 'Documentation'],
    tech: ['Python', 'FastAPI', 'Docker', 'PostgreSQL'],
    price: 'Starting from ₹50,000',
    priceNote: 'Advanced tiers: ₹1,00,000 / ₹5,00,000 / ₹10,00,000+',
    externalQuote: null,
  },
  {
    icon: <FiShield size={32} />,
    title: 'Cyber Forensic Investigation',
    tagline: 'Digital truth uncovered',
    desc: 'Professional cyber forensic investigation services — from data breach analysis and digital evidence collection to incident response and security audits.',
    features: ['Digital evidence collection', 'Data breach investigation', 'Incident response', 'Security audit & reporting', 'Legal-grade documentation'],
    tech: ['Forensic Tools', 'OSINT', 'Network Analysis', 'Malware Analysis'],
    price: 'Based on scope of investigation',
    priceNote: 'Contact us for a confidential assessment',
    externalQuote: null,
  },
];

const ServicesPage = () => {
  return (
    <div className="min-h-screen pt-32 pb-24">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 mb-20 text-center">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="font-mono text-xs text-purple-400 tracking-widest uppercase mb-4">
          Our Services
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="font-display text-7xl font-light text-white mb-6">
          Capabilities Built<br />for <em className="accent-text not-italic">Modern Business</em>
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="font-body text-white/50 max-w-lg mx-auto">
          End-to-end digital solutions — strategy, design, development, and growth — delivered with premium craftsmanship.
        </motion.p>
      </div>

      {/* Services */}
      <div className="max-w-7xl mx-auto px-6 space-y-8">
        {services.map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.05 }}
            className="glass-card p-8 md:p-12 group hover:border-purple-500/40 transition-all duration-500"
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
              {/* Left */}
              <div className="lg:col-span-1">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                  style={{ background: 'linear-gradient(135deg, rgba(127,4,155,0.4), rgba(28,12,25,0.6))' }}>
                  <span className="text-purple-400">{s.icon}</span>
                </div>
                <p className="font-mono text-xs text-purple-400 tracking-widest uppercase mb-2">{s.tagline}</p>
                <h2 className="font-display text-3xl font-medium text-white mb-4">{s.title}</h2>
                <p className="font-body text-sm text-white/50 leading-relaxed mb-6">{s.desc}</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {s.tech.map(t => (
                    <span key={t} className="px-3 py-1 rounded-full text-xs font-mono text-purple-300 border border-purple-500/30">
                      {t}
                    </span>
                  ))}
                </div>
                <p className="font-display text-xl text-white/70">{s.price}</p>
                {s.priceNote && (
                  <p className="font-body text-sm text-white/40 mt-1">{s.priceNote}</p>
                )}
              </div>

              {/* Features */}
              <div className="lg:col-span-1">
                <p className="font-body text-xs font-medium text-white/30 tracking-widest uppercase mb-4">Includes</p>
                <ul className="space-y-3">
                  {s.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-3">
                      <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: 'rgba(127,4,155,0.3)' }}>
                        <FiCheck size={11} className="text-purple-400" />
                      </span>
                      <span className="font-body text-sm text-white/60">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA */}
              <div className="lg:col-span-1 flex flex-col items-start lg:items-end justify-between h-full">
                {s.externalQuote ? (
                  <a
                    href={s.externalQuote}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center gap-2 self-start lg:self-end"
                  >
                    Get a Quote <FiArrowRight />
                  </a>
                ) : (
                  <Link to="/contact" className="btn-primary inline-flex items-center gap-2 self-start lg:self-end">
                    Get a Quote <FiArrowRight />
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Bottom CTA */}
      <div className="max-w-4xl mx-auto px-6 mt-24 text-center">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          className="glass-card p-12">
          <h2 className="font-display text-4xl text-white mb-4">Not sure which service you need?</h2>
          <p className="font-body text-white/50 mb-8">Book a free 30-minute discovery call — we'll help you figure out the best path forward.</p>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
            Book Free Consultation <FiArrowRight />
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default ServicesPage;
